"use client";

import Image from "next/image";
import { useState } from "react";

import { cn } from "~/lib/utils";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import { useTrackEvent } from "~/hooks/useTrackEvent";

import type { SourceCard } from "../data/types";
import { PlusIcon } from "@phosphor-icons/react/ssr";

type Props = {
    card: SourceCard;
    className?: string;
};

function CardImage({ src, alt }: { src: string; alt: string }) {
    return (
        <div className="relative h-[140px] w-[132px] shrink-0 overflow-hidden rounded-[12px] transition-all duration-300 ease-in-out group-hover:scale-105">
            <Image
                src={src}
                alt={alt}
                fill
                className="object-cover"
                sizes="132px"
            />
        </div>
    );
}

function ToggleIcon({ opened }: { opened: boolean }) {
    return (
        <div
            className={cn(
                "absolute right-0 top-0 z-20 flex h-8 w-8 items-center justify-center rounded-full border-[2px] transition-transform duration-300",
                opened
                    ? "rotate-45 border-pureWhite/25 bg-pureWhite/5"
                    : "border-transparent bg-[#2446AA]"
            )}
        >
            <PlusIcon size={20} className="text-pureWhite" />
        </div>
    );
}

export default function SourceCard({ card, className }: Props) {
    const sendEvent = useTrackEvent();
    const [isOpened, setIsOpened] = useState(false);

    const mobileDescription = card.descriptionMobile ?? card.description;

    const handleToggle = () => {
        if (!isOpened) {
            sendEvent({
                event: "user_interaction",
                value: {
                    event_name: GTM_EVENT_NAMES.LP_INTERACTION,
                    widget: "our_sources",
                    meta_data: {
                        click_text: card.title,
                    },
                },
            });
        }

        setIsOpened(prev => !prev);
    };

    return (
        <button
            type="button"
            aria-expanded={isOpened}
            onClick={handleToggle}
            className={cn(
                "group relative h-[172px] w-full min-w-0 shrink-0 rounded-[12px] border-0 bg-transparent p-0 text-left font-suisseBP appearance-none",
                className
            )}
        >
            <div className="grid [perspective:1000px] relative h-full w-full rounded-[12px]">
                <div
                    className={cn(
                        "col-start-1 row-start-1 rounded-[12px] bg-primary100 p-4 text-primary500 duration-300",
                        isOpened ? "opacity-0" : "opacity-100"
                    )}
                    style={{
                        transform: isOpened
                            ? "translateY(50%) rotateX(-90deg)"
                            : "translateY(0) rotateX(0deg)",
                    }}
                >
                    <div className="relative flex h-full items-start gap-4">
                        <CardImage src={card.image} alt={card.imageAlt} />

                        <div className="relative flex h-full min-w-0 flex-1 flex-col pr-10">
                            <div className="flex min-h-[140px] min-w-0 flex-1">
                                <div className="flex min-w-0 flex-1 flex-col justify-start">
                                    <h3 className="hidden lg:block text-[16px] uppercase leading-[1.25] tracking-[1.28px] lg:max-w-[362px] lg:whitespace-pre-line">
                                        {card.title}
                                    </h3>
                                    <p className="lg:hidden text-[16px] font-normal uppercase leading-[1.25] tracking-[1.28px] whitespace-pre-line">
                                        {card.mobileTitle ?? card.title}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <ToggleIcon opened={false} />
                    </div>
                </div>

                <div
                    className={cn(
                        "col-start-1 row-start-1 rounded-[18px] bg-[#14244F] p-4 text-white duration-300",
                        isOpened ? "opacity-100" : "opacity-0"
                    )}
                    style={{
                        transform: isOpened
                            ? "translateY(0) rotateX(0deg)"
                            : "translateY(-50%) rotateX(90deg)",
                    }}
                >
                    <div className="relative flex h-full items-start gap-3">
                        <CardImage src={card.image} alt={card.imageAlt} />

                        <div className="relative flex h-full min-w-0 flex-1 flex-col lg:pr-14">
                            <div className="min-w-0 flex flex-col lg:h-full lg:items-start lg:justify-between">
                                <div className="min-w-0 flex-1 lg:hidden">
                                    <p className="whitespace-pre-line text-[18px] leading-[1.2]">
                                        <span
                                            aria-hidden
                                            className="float-right ml-2 block h-[43px] w-[43px]"
                                        />
                                        {mobileDescription}
                                    </p>
                                    <div className="clear-both" />
                                </div>

                                <p className="hidden text-[16px] uppercase leading-[1.25] tracking-[1.28px] lg:block lg:max-w-[362px] lg:whitespace-pre-line">
                                    {card.title}
                                </p>

                                <p className="mt-3 hidden w-full min-w-0 text-[22px] leading-[1.2] lg:block">
                                    {card.description}
                                </p>
                            </div>
                        </div>

                        <ToggleIcon opened />
                    </div>
                </div>
            </div>
        </button>
    );
}
